/**
 * Screen the customer sees while they hold the seller's iPad.
 * Confirm and return both end on the hand-back. The handoff record stays
 * until the seller taps that button, so the shell cannot mount in their hands.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { useAuth } from "@/contexts/AuthContext";
import {
  AssinaturaServiceError,
  confirmPresencialSignature,
  loadPresencialAreas,
  loadPresencialBrandName,
  readPresencialOriginalUrl,
  type PresencialArea,
} from "@/features/proposta/services/proposta-assinatura.service";
import { clearHandoff, handoffPersistKey, type PresencialHandoff } from "./handoff";
import { KioskDocument, KioskHandback, KioskStatus } from "./kiosk-surface";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const OPEN_FAILED = "Não foi possível abrir a proposta.";
const NETWORK_FAILED =
  "Não foi possível conectar. Tente de novo ou toque em Cancelar.";

type Phase = "loading" | "failed" | "ready" | "handback";

interface Props {
  record: PresencialHandoff;
}

function terminalCode(err: unknown): boolean {
  return (
    err instanceof AssinaturaServiceError &&
    (err.code === "already_signed" || err.code === "sessao_already_terminal")
  );
}

function customerMessage(err: unknown, fallback: string): string {
  if (err instanceof AssinaturaServiceError && err.code === "network") return NETWORK_FAILED;
  if (err instanceof AssinaturaServiceError) return err.message;
  return fallback;
}

export default function PresencialKiosk({ record }: Props): React.JSX.Element {
  const { user, loading: authLoading, pessoa } = useAuth();
  const tenantId = typeof pessoa?.tenant_id === "string" ? pessoa.tenant_id : null;
  const signedIn = !authLoading && user != null;

  const [phase, setPhase] = useState<Phase>("loading");
  const [attempt, setAttempt] = useState(0);
  const [brandName, setBrandName] = useState("");
  const [pdf, setPdf] = useState<Uint8Array | null>(null);
  const [areas, setAreas] = useState<PresencialArea[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [stamps, setStamps] = useState<ReadonlyMap<string, string>>(() => new Map());
  const [adopted, setAdopted] = useState<string | null>(null);
  const [activeAreaId, setActiveAreaId] = useState<string | null>(null);
  const [redrawing, setRedrawing] = useState(false);
  const [pagesReady, setPagesReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const handedBack = useRef(false);

  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    void loadPresencialBrandName(tenantId).then((name) => {
      if (!cancelled) setBrandName(name);
    });
    return () => {
      cancelled = true;
    };
  }, [signedIn, tenantId]);

  useEffect(() => {
    if (!signedIn || handedBack.current) return;
    let cancelled = false;
    setPhase("loading");
    setPagesReady(false);
    setPdf(null);
    setStatusMessage(null);
    void (async () => {
      try {
        const [url, loadedAreas] = await Promise.all([
          readPresencialOriginalUrl(record.sessaoId),
          loadPresencialAreas(record.sessaoId, record.signatarioIndice),
        ]);
        const response = await fetch(url);
        if (!response.ok) throw new Error("pdf-bytes");
        const bytes = new Uint8Array(await response.arrayBuffer());
        if (cancelled || handedBack.current) return;
        setAreas(loadedAreas);
        setPdf(bytes);
        setPhase("ready");
      } catch (err) {
        if (cancelled || handedBack.current) return;
        setStatusMessage(customerMessage(err, OPEN_FAILED));
        setPhase("failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [signedIn, record.sessaoId, record.signatarioIndice, attempt]);

  const handleReturn = useCallback(() => {
    if (submitting) return;
    handedBack.current = true;
    setActiveAreaId(null);
    setPhase("handback");
  }, [submitting]);

  const handleAreaClick = useCallback(
    (area: PresencialArea) => {
      if (submitting || !pagesReady) return;
      if (stamps.has(area.id)) return;
      if (adopted) {
        setStamps((prev) => {
          const next = new Map(prev);
          next.set(area.id, adopted);
          return next;
        });
        return;
      }
      setRedrawing(false);
      setActiveAreaId(area.id);
    },
    [adopted, pagesReady, stamps, submitting],
  );

  const handleModalConfirm = useCallback(
    (pngBase64: string) => {
      if (!activeAreaId || submitting) return;
      setAdopted(pngBase64);
      setStamps((prev) => {
        if (!redrawing) {
          const next = new Map(prev);
          next.set(activeAreaId, pngBase64);
          return next;
        }
        const next = new Map<string, string>();
        prev.forEach((_, id) => next.set(id, pngBase64));
        next.set(activeAreaId, pngBase64);
        return next;
      });
      setActiveAreaId(null);
      setRedrawing(false);
    },
    [activeAreaId, redrawing, submitting],
  );

  const handleRedraw = useCallback(() => {
    if (submitting) return;
    const anchor = Array.from(stamps.keys())[0] ?? areas[0]?.id;
    if (!anchor) return;
    setRedrawing(true);
    setActiveAreaId(anchor);
  }, [areas, stamps, submitting]);

  const handleFinalize = useCallback(async () => {
    if (!adopted || submitting || !pagesReady) return;
    setSubmitting(true);
    setBanner(null);
    setActiveAreaId(null);
    try {
      await confirmPresencialSignature(
        record.sessaoId,
        record.signatarioIndice,
        adopted,
      );
      handedBack.current = true;
      setPhase("handback");
    } catch (err) {
      if (terminalCode(err)) {
        handedBack.current = true;
        setPhase("handback");
        return;
      }
      setBanner(customerMessage(err, NETWORK_FAILED));
    } finally {
      setSubmitting(false);
    }
  }, [adopted, pagesReady, record.sessaoId, record.signatarioIndice, submitting]);

  if (!authLoading && !user) {
    return <KioskHandback onRelease={clearHandoff} />;
  }
  if (phase === "handback") {
    return <KioskHandback onRelease={clearHandoff} />;
  }
  if (phase !== "ready" || !pdf) {
    return (
      <KioskStatus
        nome={record.nome}
        brandName={brandName}
        message={phase === "failed" ? statusMessage : null}
        onReturn={handleReturn}
        onRetry={
          phase === "failed"
            ? () => {
                handedBack.current = false;
                setAttempt((value) => value + 1);
              }
            : undefined
        }
      />
    );
  }

  const activeArea = areas.find((area) => area.id === activeAreaId) ?? null;
  return (
    <KioskDocument
      pdf={pdf}
      areas={areas}
      nome={record.nome}
      brandName={brandName}
      stamps={stamps}
      adopted={adopted}
      submitting={submitting}
      pagesReady={pagesReady}
      banner={banner}
      activeArea={activeArea}
      persistKey={handoffPersistKey(record)}
      onAreaClick={handleAreaClick}
      onRedraw={handleRedraw}
      onFinalize={() => {
        void handleFinalize();
      }}
      onReturn={handleReturn}
      onModalConfirm={handleModalConfirm}
      onModalClose={() => {
        setActiveAreaId(null);
        setRedrawing(false);
      }}
      onPagesReady={() => setPagesReady(true)}
      onPreviewError={() => {
        if (handedBack.current) return;
        setStatusMessage(OPEN_FAILED);
        setPhase("failed");
      }}
    />
  );
}
